// src/pages/admin/ManageUsers.js

import React, { useState, useEffect } from 'react';
import { 
  FaUser, FaEdit, FaBan, FaCheckCircle, FaTrash, FaEye, FaKey,
  FaSearch, FaFilter, FaUsers, FaUserShield, FaUserCheck, FaUserTimes,
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaChartLine, FaTag
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedUser(data);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleBlockUser = async (userId, reason) => {
    const blockReason = reason || prompt('Enter reason for blocking (optional):');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}/block`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: blockReason })
      });

      if (response.ok) {
        alert('User blocked successfully');
        fetchUsers();
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Failed to block user');
    }
  };

  const handleUnblockUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}/unblock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        alert('User unblocked successfully');
        fetchUsers();
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('Error unblocking user:', error);
      alert('Failed to unblock user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        alert('User deleted successfully');
        fetchUsers();
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt('Enter new password (min 6 characters):');
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword })
      });

      if (response.ok) {
        alert('Password reset successfully');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${editForm.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        alert('User updated successfully');
        setShowEditModal(false);
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const openEditModal = (user) => {
    setEditForm({
      id: user.id || user._id,
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      city: user.city || '',
      state: user.state || '',
      country: user.country || '',
      role: user.role || 'client',
      status: user.status || 'active',
      customField: user.customField || '' // ✅ NEW: Custom field for admin allocation
    });
    setShowEditModal(true);
    setSelectedUser(null);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole || (!user.role && filterRole === 'client');
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus || (!user.status && filterStatus === 'active');

    return matchesSearch && matchesRole && matchesStatus;
  });

  const stats = {
    total: users.length,
    active: users.filter(u => u.status !== 'blocked').length,
    blocked: users.filter(u => u.status === 'blocked').length,
    admins: users.filter(u => u.role === 'admin').length,
    clients: users.filter(u => u.role === 'client' || !u.role).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white flex items-center gap-3 mb-2">
          <FaUsers className="text-blue-600" />
          User Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400">Manage and monitor all users</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
          <FaUsers className="text-3xl mb-3 opacity-80" />
          <div className="text-sm mb-1 opacity-90">Total Users</div>
          <div className="text-4xl font-bold">{stats.total}</div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
          <FaUserCheck className="text-3xl mb-3 opacity-80" />
          <div className="text-sm mb-1 opacity-90">Active Users</div>
          <div className="text-4xl font-bold">{stats.active}</div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
          <FaUserTimes className="text-3xl mb-3 opacity-80" />
          <div className="text-sm mb-1 opacity-90">Blocked</div>
          <div className="text-4xl font-bold">{stats.blocked}</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
          <FaUserShield className="text-3xl mb-3 opacity-80" />
          <div className="text-sm mb-1 opacity-90">Admins</div>
          <div className="text-4xl font-bold">{stats.admins}</div>
        </div>

        <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
          <FaUser className="text-3xl mb-3 opacity-80" />
          <div className="text-sm mb-1 opacity-90">Clients</div>
          <div className="text-4xl font-bold">{stats.clients}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <FaSearch className="inline mr-2" />
              Search Users
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <FaFilter className="inline mr-2" />
              Filter by Role
            </label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admins</option>
              <option value="client">Clients</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <FaUsers className="text-6xl text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Activity</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => (
                  <tr key={user.id || user._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-3">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 dark:text-white">{user.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <FaEnvelope className="text-xs" />
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        <FaPhone className="text-xs text-green-600" />
                        {user.phone || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <FaMapMarkerAlt className="text-xs text-red-600" />
                        {user.city ? `${user.city}, ${user.country || ''}` : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        user.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' 
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      }`}>
                        {user.role === 'admin' ? <FaUserShield className="mr-1" /> : <FaUser className="mr-1" />}
                        {user.role || 'client'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        user.status === 'blocked' 
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}>
                        {user.status === 'blocked' ? <FaBan className="mr-1" /> : <FaCheckCircle className="mr-1" />}
                        {user.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        <FaChartLine className="text-blue-600" />
                        {user.activityCount || 0} actions
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <FaClock className="text-gray-400" />
                        {user.sessionCount || 0} sessions
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => fetchUserDetails(user.id || user._id)}
                          className="p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900 text-blue-600 transition-colors"
                          title="View Details"
                        >
                          <FaEye size={18} />
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-900 text-green-600 transition-colors"
                          title="Edit User"
                        >
                          <FaEdit size={18} />
                        </button>
                        {user.status === 'blocked' ? (
                          <button
                            onClick={() => handleUnblockUser(user.id || user._id)}
                            className="p-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-900 text-green-600 transition-colors"
                            title="Unblock User"
                          >
                            <FaCheckCircle size={18} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBlockUser(user.id || user._id)}
                            className="p-2 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900 text-orange-600 transition-colors"
                            title="Block User"
                          >
                            <FaBan size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleResetPassword(user.id || user._id)}
                          className="p-2 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900 text-purple-600 transition-colors"
                          title="Reset Password"
                        >
                          <FaKey size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id || user._id)}
                          className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 text-red-600 transition-colors"
                          title="Delete User"
                        >
                          <FaTrash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Edit User</h2>
                  <p className="text-sm opacity-90 mt-1">Update user information</p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <form onSubmit={handleUpdateUser} className="p-6">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={editForm.city}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Role
                  </label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="client">Client</option>
                    <option value="admin">Admin</option>
                    <option value="support_agent">Support Agent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="active">Active</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>

                {/* ✅ NEW: Custom Field for Admin Allocation */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <FaTag className="inline mr-2" />
                    Custom Admin Field
                  </label>
                  <input
                    type="text"
                    value={editForm.customField}
                    onChange={(e) => setEditForm({ ...editForm, customField: e.target.value })}
                    placeholder="e.g., Premium User, VIP, Department X, Assigned to Manager Y"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Use this field to allocate or categorize users as needed
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 font-semibold transition-all transform hover:scale-105 shadow-lg"
                >
                  Update User
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-4 border-2 border-gray-300 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold transition-colors text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Details Modal - UNCHANGED */}
      {selectedUser && selectedUser.user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">User Details</h2>
                  <p className="text-sm opacity-90 mt-1">Complete user profile and activity</p>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* User Info */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FaUser className="text-blue-600" />
                  User Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { label: 'Name', value: selectedUser.user.name },
                    { label: 'Email', value: selectedUser.user.email },
                    { label: 'Phone', value: selectedUser.user.phone || 'N/A' },
                    { label: 'Location', value: selectedUser.user.city ? `${selectedUser.user.city}, ${selectedUser.user.state || ''} ${selectedUser.user.country || ''}` : 'N/A' },
                    { label: 'Role', value: selectedUser.user.role || 'client' },
                    { label: 'Status', value: selectedUser.user.status || 'active' },
                    { label: 'Joined', value: new Date(selectedUser.user.createdAt).toLocaleDateString() },
                    { label: 'Last Login', value: selectedUser.user.lastLogin ? new Date(selectedUser.user.lastLogin).toLocaleDateString() : 'Never' },
                    { label: 'Custom Field', value: selectedUser.user.customField || 'N/A' }
                  ].map((item, idx) => (
                    <div key={idx} className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                      <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 block mb-1">{item.label}</label>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Statistics */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FaChartLine className="text-green-600" />
                  Activity Statistics
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/50 p-6 rounded-xl text-center">
                    <div className="text-4xl font-bold text-blue-600 mb-2">{selectedUser.stats?.totalSessions || 0}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Sessions</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-900/50 p-6 rounded-xl text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">{selectedUser.stats?.totalActivity || 0}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Actions</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-900/50 p-6 rounded-xl text-center">
                    <div className="text-4xl font-bold text-purple-600 mb-2">
                      {Math.floor((selectedUser.stats?.totalTimeOnSite || 0) / 60)}m
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Time on Site</div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              {selectedUser.recentActivity && selectedUser.recentActivity.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 max-h-60 overflow-y-auto">
                    <div className="space-y-2">
                      {selectedUser.recentActivity.slice(0, 10).map((activity, index) => (
                        <div key={index} className="flex justify-between items-center text-sm p-3 bg-white dark:bg-gray-800 rounded-lg">
                          <span className="text-gray-700 dark:text-gray-300 font-medium capitalize">
                            {activity.action.replace(/_/g, ' ')}
                          </span>
                          <span className="text-gray-500 text-xs">{new Date(activity.timestamp).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  onClick={() => openEditModal(selectedUser.user)}
                  className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 flex items-center justify-center gap-2 font-semibold transition-all transform hover:scale-105"
                >
                  <FaEdit /> Edit User
                </button>
                {selectedUser.user.status === 'blocked' ? (
                  <button
                    onClick={() => handleUnblockUser(selectedUser.user.id || selectedUser.user._id)}
                    className="px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 flex items-center justify-center gap-2 font-semibold transition-all transform hover:scale-105"
                  >
                    <FaCheckCircle /> Unblock User
                  </button>
                ) : (
                  <button
                    onClick={() => handleBlockUser(selectedUser.user.id || selectedUser.user._id)}
                    className="px-6 py-4 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-xl hover:from-orange-700 hover:to-orange-800 flex items-center justify-center gap-2 font-semibold transition-all transform hover:scale-105"
                  >
                    <FaBan /> Block User
                  </button>
                )}
                <button
                  onClick={() => handleResetPassword(selectedUser.user.id || selectedUser.user._id)}
                  className="px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 flex items-center justify-center gap-2 font-semibold transition-all transform hover:scale-105"
                >
                  <FaKey /> Reset Password
                </button>
                <button
                  onClick={() => handleDeleteUser(selectedUser.user.id || selectedUser.user._id)}
                  className="px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 flex items-center justify-center gap-2 font-semibold transition-all transform hover:scale-105"
                >
                  <FaTrash /> Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;